import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import { BadgeCheck, Lock } from "lucide-react";

export default function TeamShared() {
  const { token } = useParams();
  const [data, setData] = useState(undefined);
  useEffect(() => {
    api.get(`/public/team/${token}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [token]);

  if (data === undefined) return <div className="min-h-screen grid place-items-center text-muted font-mono text-sm">Loading team brief…</div>;
  if (data === null) return (
    <div className="min-h-screen grid place-items-center p-6 text-center">
      <div><Lock className="mx-auto text-muted mb-4" size={36} /><p className="font-display font-bold text-xl">Link unavailable</p>
        <p className="text-muted text-sm mt-1">This shared team link is invalid or was revoked.</p>
        <Link to="/" className="text-azure text-sm mt-4 inline-block">Go to Team Assembly →</Link></div>
    </div>
  );

  const Panel = ({ title, items, color, ordered }) => (!items || !items.length) ? null : (
    <div className="rounded-2xl border border-white/10 bg-surface p-5">
      <p className={`label-mono text-${color} mb-3`}>{title}</p>
      <ul className="space-y-1.5 text-sm text-muted">
        {items.map((it, i) => <li key={i} className="flex gap-2"><span className="text-faint font-mono text-xs">{ordered ? `${i + 1}.` : "·"}</span> {it}</li>)}
      </ul>
    </div>
  );

  return (
    <div className="min-h-screen ta-noise">
      <header className="ta-glass border-b border-white/10">
        <div className="mx-auto max-w-5xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <Logo />
          <span className="label-mono text-faint">Shared team brief</span>
        </div>
      </header>
      <div className="mx-auto max-w-5xl px-5 lg:px-8 py-10 relative z-10">
        <p className="label-mono text-azure mb-1">{data.team_name}</p>
        <h1 className="font-display font-bold text-3xl mb-2">Recommended technical team</h1>
        {data.analysis?.summary && <p className="text-sm text-muted mb-8 max-w-2xl">{data.analysis.summary}</p>}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {(data.roles || []).map((r, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-surface p-5">
              <p className="label-mono text-faint mb-2">{r.role_title}</p>
              {r.member ? (<>
                <div className="flex items-center justify-between">
                  <p className="font-display font-bold text-ink">{r.member.card.display_name}</p>
                  <span className="font-mono text-azure font-bold">{r.member.score}</span>
                </div>
                <p className="text-xs text-muted mt-0.5">{r.member.card.headline}</p>
                <div className="mt-2 flex flex-wrap gap-1">{(r.member.card.top_skills || []).slice(0, 4).map((s) => <span key={s} className="rounded bg-white/5 px-1.5 py-0.5 text-[10px] font-mono text-muted">{s}</span>)}</div>
                {r.member.card.verification_level && <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald/10 px-2 py-0.5 text-[10px] text-emerald"><BadgeCheck size={10} /> {(r.member.card.verification_level).replace(/_/g, " ")}</span>}
              </>) : <p className="text-sm text-faint">No candidate for this role yet.</p>}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Panel title="Team strengths" items={data.analysis?.strengths} color="emerald" />
          <Panel title="Missing capabilities" items={data.analysis?.missing_capabilities} color="signal" />
          <Panel title="Working-style risks" items={data.analysis?.working_style_risks} color="amber" />
          <Panel title="Recommended interview sequence" items={data.analysis?.interview_sequence} color="azure" ordered />
        </div>
        <p className="mt-10 text-xs text-faint">Shared via Team Assembly · AI-assisted decision support — human review required · Confidential.</p>
      </div>
    </div>
  );
}
